// SignalRService.js OPTIMISÉ pour l'interface CLIENT
import * as signalR from '@microsoft/signalr';

class SignalRService {
    constructor() {
        this.connection = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isConnecting = false;
        this.connectionStatusCallbacks = [];
        this.connectionId = null;
        this.activeGroups = new Set();
        this.newCommentCallbacks = [];
        this.readStatusCallbacks = [];
        this.statusChangeCallbacks = [];
        this.isInitialized = false;

        // Configuration dynamique de l'URL pour l'interface CLIENT
        // Configuration dynamique de l'URL
        this.API_BASE_URL = 'https://resolvebo.innovationcode.pro';
        // this.API_BASE_URL = 'http://localhost:5000';
        this.connectionUrl = `${this.API_BASE_URL}/notificationHub`;

        console.log(`SignalR CLIENT configuré pour: ${this.connectionUrl}`);

        // Auto-initialisation
        this.initialize();
    }

    async initialize() {
        if (this.isInitialized) return;

        try {
            await this.start();
            this.isInitialized = true;
            console.log('SignalR CLIENT: Initialisation terminée');
        } catch (error) {
            console.error('SignalR CLIENT: Erreur d\'initialisation:', error);
            // Réessayer après 3 secondes
            setTimeout(() => this.initialize(), 3000);
        }
    }

    async start() {
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            console.log("SignalR CLIENT: Connexion déjà active");
            return true;
        }

        if (this.isConnecting) {
            console.log("SignalR CLIENT: Connexion déjà en cours");
            return false;
        }

        this.isConnecting = true;
        this._updateConnectionStatus('connecting');

        try {
            if (!this.connection) {
                this.connection = new signalR.HubConnectionBuilder()
                    .withUrl(this.connectionUrl, {
                        skipNegotiation: false,
                        transport: signalR.HttpTransportType.WebSockets |
                            signalR.HttpTransportType.ServerSentEvents |
                            signalR.HttpTransportType.LongPolling,
                        withCredentials: false
                    })
                    .withAutomaticReconnect({
                        nextRetryDelayInMilliseconds: (retryContext) => {
                            const delays = [0, 2000, 5000, 10000, 15000, 30000];
                            return delays[Math.min(retryContext.previousRetryCount, delays.length - 1)];
                        }
                    })
                    .configureLogging(signalR.LogLevel.Information)
                    .build();

                this._setupConnectionHandlers();
            }

            await this.connection.start();
            console.log("SignalR CLIENT: Connexion établie avec succès");
            this.connectionId = this.connection.connectionId;
            this.isConnecting = false;
            this.reconnectAttempts = 0;
            this._updateConnectionStatus('connected');

            this._setupEventHandlers();
            await this._rejoinGroups();

            return true;
        } catch (error) {
            console.error("SignalR CLIENT: Erreur lors de la connexion", error);
            this.isConnecting = false;
            this._updateConnectionStatus('error', error.message);

            this._scheduleReconnect();
            return false;
        }
    }

    _setupConnectionHandlers() {
        this.connection.onreconnecting(() => {
            console.log("SignalR CLIENT: Tentative de reconnexion...");
            this._updateConnectionStatus('reconnecting');
        });

        this.connection.onreconnected((connectionId) => {
            console.log(`SignalR CLIENT: Reconnecté (ID: ${connectionId})`);
            this.connectionId = connectionId;
            this._updateConnectionStatus('connected');
            this._rejoinGroups();
        });

        this.connection.onclose((error) => {
            console.log("SignalR CLIENT: Connexion fermée", error);
            this._updateConnectionStatus('disconnected');
            this._scheduleReconnect();
        });
    }

    _setupEventHandlers() {
        if (!this.connection) return;

        // Supprimer les anciens handlers
        this.connection.off("ReceiveNewComment");
        this.connection.off("ReceiveCommentReadStatus");
        this.connection.off("ReceiveStatusChange");

        this.connection.on("ReceiveNewComment", (commentData) => {
            console.log("🔥 SignalR CLIENT: Nouveau commentaire reçu", commentData);

            // Vérifier que c'est bien un message de l'agent vers le client
            if (commentData.authorRole === 'backoffice' || commentData.authorRole === 'agent') {
                console.log("🎯 SignalR CLIENT: Message d'agent détecté, traitement...");

                this.newCommentCallbacks.forEach(callback => {
                    try {
                        callback(commentData);
                    } catch (error) {
                        console.error("Erreur dans callback nouveau commentaire:", error);
                    }
                });
            } else {
                console.log("📝 SignalR CLIENT: Message client ignoré (normal)");
            }
        });

        this.connection.on("ReceiveCommentReadStatus", (readStatus) => {
            console.log("👁️ SignalR CLIENT: Statut de lecture mis à jour", readStatus);
            this.readStatusCallbacks.forEach(callback => {
                try {
                    callback(readStatus);
                } catch (error) {
                    console.error("Erreur dans callback statut de lecture:", error);
                }
            });
        });

        this.connection.on("ReceiveStatusChange", (statusData) => {
            console.log("📋 SignalR CLIENT: Changement de statut de réclamation", statusData);
            this.statusChangeCallbacks.forEach(callback => {
                try {
                    callback(statusData);
                } catch (error) {
                    console.error("Erreur dans callback changement de statut:", error);
                }
            });
        });

        console.log("✅ SignalR CLIENT: Event handlers configurés");
    }

    _scheduleReconnect() {
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
            console.log(`SignalR CLIENT: Reconnexion prévue dans ${delay}ms (tentative ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);

            setTimeout(() => {
                this.reconnectAttempts++;
                this.start();
            }, delay);
        } else {
            console.error("SignalR CLIENT: Nombre maximal de tentatives de reconnexion atteint");
        }
    }

    async _rejoinGroups() {
        if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
            console.warn("SignalR CLIENT: Impossible de rejoindre les groupes, pas de connexion");
            return;
        }

        try {
            const groupsArray = Array.from(this.activeGroups);
            for (const groupName of groupsArray) {
                const trackingNumber = groupName.replace('claim-', '');
                console.log(`🔄 SignalR CLIENT: Rejoindre le groupe ${trackingNumber} après reconnexion`);
                await this.connection.invoke("JoinClaimGroup", trackingNumber);
            }
            console.log("✅ SignalR CLIENT: Tous les groupes ont été rejoints");
        } catch (error) {
            console.error("SignalR CLIENT: Erreur lors de la reconnexion aux groupes", error);
        }
    }

    _updateConnectionStatus(status, errorMessage = null) {
        const statusInfo = {
            status,
            timestamp: new Date(),
            connectionId: this.connectionId,
            errorMessage
        };

        this.connectionStatusCallbacks.forEach(callback => {
            try {
                callback(statusInfo);
            } catch (error) {
                console.error("Erreur dans callback statut de connexion:", error);
            }
        });
    }

    async joinClaimGroup(trackingNumber) {
        if (!trackingNumber) {
            console.error("SignalR CLIENT: Numéro de suivi non fourni");
            return false;
        }

        const groupName = `claim-${trackingNumber}`;

        if (this.activeGroups.has(groupName)) {
            console.log(`✅ SignalR CLIENT: Déjà membre du groupe ${groupName}`);
            return true;
        }

        try {
            // S'assurer que la connexion est établie
            if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
                console.log("SignalR CLIENT: Connexion non établie, tentative de connexion...");
                const connected = await this.start();
                if (!connected) {
                    throw new Error("Impossible d'établir la connexion");
                }
            }

            await this.connection.invoke("JoinClaimGroup", trackingNumber);
            console.log(`🎯 SignalR CLIENT: Groupe rejoint avec succès: ${groupName}`);

            this.activeGroups.add(groupName);
            return true;
        } catch (error) {
            console.error(`❌ SignalR CLIENT: Erreur lors de la connexion au groupe ${groupName}`, error);
            return false;
        }
    }

    async leaveClaimGroup(trackingNumber) {
        if (!trackingNumber) return;

        const groupName = `claim-${trackingNumber}`;

        try {
            if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
                await this.connection.invoke("LeaveClaimGroup", trackingNumber);
                console.log(`🚪 SignalR CLIENT: Groupe quitté: ${groupName}`);
            }

            this.activeGroups.delete(groupName);
        } catch (error) {
            console.error(`❌ SignalR CLIENT: Erreur lors de la déconnexion du groupe ${groupName}`, error);
        }
    }

    onConnectionStatusChanged(callback) {
        this.connectionStatusCallbacks.push(callback);
        return () => {
            this.connectionStatusCallbacks = this.connectionStatusCallbacks.filter(cb => cb !== callback);
        };
    }

    onNewComment(callback) {
        console.log("📝 SignalR CLIENT: Ajout callback nouveau commentaire");
        this.newCommentCallbacks.push(callback);
        return () => {
            console.log("🗑️ SignalR CLIENT: Suppression callback nouveau commentaire");
            this.newCommentCallbacks = this.newCommentCallbacks.filter(cb => cb !== callback);
        };
    }

    onCommentReadStatus(callback) {
        this.readStatusCallbacks.push(callback);
        return () => {
            this.readStatusCallbacks = this.readStatusCallbacks.filter(cb => cb !== callback);
        };
    }

    onStatusChange(callback) {
        this.statusChangeCallbacks.push(callback);
        return () => {
            this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
        };
    }

    async stop() {
        if (this.connection) {
            try {
                await this.connection.stop();
                console.log("SignalR CLIENT: Connexion fermée manuellement");
                this.activeGroups.clear();
                this._updateConnectionStatus('disconnected');
            } catch (error) {
                console.error("SignalR CLIENT: Erreur lors de la fermeture de la connexion", error);
            }
        }
    }

    getConnectionState() {
        return this.connection ? this.connection.state : 'Disconnected';
    }

    getDebugInfo() {
        return {
            connectionState: this.getConnectionState(),
            connectionId: this.connectionId,
            activeGroups: Array.from(this.activeGroups),
            reconnectAttempts: this.reconnectAttempts,
            connectionUrl: this.connectionUrl,
            callbacksCount: {
                newComment: this.newCommentCallbacks.length,
                readStatus: this.readStatusCallbacks.length,
                statusChange: this.statusChangeCallbacks.length
            }
        };
    }

    // Méthode de test pour simuler un message
    async testReceiveMessage(trackingNumber) {
        const testMessage = {
            id: `test-${Date.now()}`,
            content: "Message de test depuis le backoffice",
            authorName: "Agent Test",
            authorRole: "backoffice",
            createdAt: new Date(),
            isInternal: false,
            isReadByClient: false,
            isReadByBank: true
        };

        console.log("🧪 SignalR CLIENT: Test simulation message", testMessage);

        this.newCommentCallbacks.forEach(callback => {
            try {
                callback(testMessage);
            } catch (error) {
                console.error("Erreur dans test callback:", error);
            }
        });
    }
}

// Singleton global
const signalRService = new SignalRService();

// Export global pour debug
if (typeof window !== 'undefined') {
    window.signalRServiceClient = signalRService;
}

export default signalRService;